/**
 * KpiDetailModal — reusable modal that shows a breakdown table
 * for whichever KPI card the user clicked on the Manager Dashboard.
 * Renders different table content based on the `type` prop.
 */
import React from 'react';
import { X, ClipboardList, Users, Package, TrendingUp, Loader2 } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { getStatusBadgeClass } from '../utils/statusColors';
import { formatShortDate } from '../utils/dateUtils';
import { displayRemainingCases } from '../utils/lotCases';
import { Lot, ByproductSummaryItem } from '../services/inventoryService';
import { ScheduledWorkforceResponse } from '../services/workScheduleService';

export type KpiDetailType = 'sessions' | 'workforce' | 'lots' | 'output' | null;

interface KpiDetailModalProps {
  type: KpiDetailType;
  onClose: () => void;
  // Data for each view
  todaySessions: any[];
  workforce: ScheduledWorkforceResponse | undefined;
  lots: Lot[];
  lotsLoading: boolean;
  byproducts?: ByproductSummaryItem[];
  byproductsLoading?: boolean;
}

// ── Shared table wrapper with horizontal scroll ──────────────────────────
const TableWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      {children}
    </table>
  </div>
);

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
    {children}
  </th>
);

const Td: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <td className={`px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap ${className}`}>
    {children}
  </td>
);

const getRoleLabel = (jobCode: string) => {
  if (jobCode === '84000') return 'Cutting';
  if (jobCode === '85000') return 'Packing';
  return jobCode;
};

const getWorkforceStatusLabel = (isAssigned: boolean, isScheduled?: boolean) => {
  if (isAssigned) return 'Assigned';
  if (isScheduled !== false) return 'Scheduled';
  return '—';
};

// ── Per-type content renderers ───────────────────────────────────────────

// Order in which sub-session status chips are shown (canonical lifecycle order).
const SUB_SESSION_STATUS_ORDER = ['pending', 'submitted', 'received', 'ready', 'active', 'paused', 'completed', 'cancelled'];

const SessionsContent: React.FC<{ sessions: any[]; t: (key: string) => string }> = ({ sessions, t }) => {
  if (!sessions.length) return <p className="text-gray-500 dark:text-gray-400 text-center py-6">{t('manager.kpiDetail.noData')}</p>;

  return (
    <TableWrapper>
      <thead className="bg-gray-50 dark:bg-gray-700/50">
        <tr>
          <Th>{t('manager.kpiDetail.rowNumber')}</Th>
          <Th>{t('manager.kpiDetail.sessionNumber')}</Th>
          <Th>{t('manager.kpiDetail.product')}</Th>
          <Th>{t('manager.kpiDetail.status')}</Th>
          <Th>{t('manager.kpiDetail.subSessions')}</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
        {sessions.map((s: any, index: number) => {
          const subs = s.sub_sessions || [];
          // Count sub-sessions per status (every category, including cancelled).
          const statusCounts: Record<string, number> = subs.reduce((acc: Record<string, number>, sub: any) => {
            acc[sub.status] = (acc[sub.status] || 0) + 1;
            return acc;
          }, {});
          // Canonical order first, then any unexpected/legacy statuses so none are hidden.
          const shownStatuses = [
            ...SUB_SESSION_STATUS_ORDER.filter((st) => statusCounts[st] > 0),
            ...Object.keys(statusCounts).filter((st) => !SUB_SESSION_STATUS_ORDER.includes(st)),
          ];

          return (
            <tr key={s.session_number} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
              <Td className="text-xs text-gray-400">{index + 1}</Td>
              <Td className="font-mono text-xs">{s.session_number}</Td>
              <Td>
                <div className="flex flex-col">
                  <span className="font-medium">{s.product_name || s.product_sku}</span>
                  {s.product_name && s.product_sku && (
                    <span className="text-xs text-gray-400">{s.product_sku}</span>
                  )}
                </div>
              </Td>
              <Td>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadgeClass(s.status)}`}>
                  {t(`status.${s.status}`) || s.status}
                </span>
              </Td>
              <Td>
                <div className="flex flex-wrap gap-1.5 text-xs">
                  {shownStatuses.map((st) => (
                    <span key={st} className={`px-2 py-0.5 font-medium rounded-full ${getStatusBadgeClass(st)}`}>
                      {statusCounts[st]} {t(`status.${st}`) || st}
                    </span>
                  ))}
                  {subs.length === 0 && <span className="text-gray-400">—</span>}
                </div>
              </Td>
            </tr>
          );
        })}
      </tbody>
    </TableWrapper>
  );
};

const WorkforceContent: React.FC<{ workforce: ScheduledWorkforceResponse | undefined; t: (key: string) => string }> = ({ workforce, t }) => {
  if (!workforce) return <p className="text-gray-500 dark:text-gray-400 text-center py-6">{t('manager.kpiDetail.noData')}</p>;

  const { operators, packers, stations } = workforce;

  return (
    <div className="space-y-4">
      {/* Operators */}
      {operators.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            {t('manager.kpiDetail.operatorsSection')} ({operators.length})
          </h4>
          <TableWrapper>
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <Th>{t('manager.kpiDetail.rowNumber')}</Th>
                <Th>{t('manager.kpiDetail.employeeNumber')}</Th>
                <Th>{t('manager.kpiDetail.name')}</Th>
                <Th>{t('manager.kpiDetail.role')}</Th>
                <Th>{t('manager.kpiDetail.stationName')}</Th>
                <Th>{t('manager.kpiDetail.status')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {operators.map((op, index) => (
                <tr key={op.employee_number} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <Td className="text-xs text-gray-400">{index + 1}</Td>
                  <Td className="font-mono text-xs">{op.employee_number}</Td>
                  <Td>{op.full_name}</Td>
                  <Td>{getRoleLabel(op.job_code)}</Td>
                  <Td>
                    {op.assignment_station_id ? `Station ${op.assignment_station_id}` : op.station_name || '—'}
                  </Td>
                  <Td>
                    {op.is_assigned ? (
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full w-fit ${getStatusBadgeClass(op.assignment_status || 'received')}`}>
                        {getWorkforceStatusLabel(op.is_assigned, op.is_scheduled)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">{getWorkforceStatusLabel(op.is_assigned, op.is_scheduled)}</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrapper>
        </div>
      )}

      {/* Packers */}
      {packers.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-blue-500" />
            {t('manager.kpiDetail.packersSection')} ({packers.length})
          </h4>
          <TableWrapper>
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <Th>{t('manager.kpiDetail.rowNumber')}</Th>
                <Th>{t('manager.kpiDetail.employeeNumber')}</Th>
                <Th>{t('manager.kpiDetail.name')}</Th>
                <Th>{t('manager.kpiDetail.role')}</Th>
                <Th>{t('manager.kpiDetail.stationName')}</Th>
                <Th>{t('manager.kpiDetail.status')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {packers.map((pk, index) => (
                <tr key={pk.employee_number} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <Td className="text-xs text-gray-400">{index + 1}</Td>
                  <Td className="font-mono text-xs">{pk.employee_number}</Td>
                  <Td>{pk.full_name}</Td>
                  <Td>{getRoleLabel(pk.job_code)}</Td>
                  <Td>—</Td>
                  <Td>
                    {pk.is_assigned ? (
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadgeClass(pk.assignment_status || 'received')}`}>
                        {getWorkforceStatusLabel(pk.is_assigned, pk.is_scheduled)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">{getWorkforceStatusLabel(pk.is_assigned, pk.is_scheduled)}</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrapper>
        </div>
      )}

      {/* Stations */}
      {stations.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-purple-500" />
            {t('manager.kpiDetail.stationsSection')} ({stations.length})
          </h4>
          <TableWrapper>
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <Th>{t('manager.kpiDetail.rowNumber')}</Th>
                <Th>{t('manager.kpiDetail.stationName')}</Th>
                <Th>{t('manager.kpiDetail.type')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {stations.map((st, index) => (
                <tr key={st.station_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <Td className="text-xs text-gray-400">{index + 1}</Td>
                  <Td>{st.station_name}</Td>
                  <Td>—</Td>
                </tr>
              ))}
            </tbody>
          </TableWrapper>
        </div>
      )}

      {operators.length === 0 && packers.length === 0 && stations.length === 0 && (
        <p className="text-gray-500 dark:text-gray-400 text-center py-6">{t('manager.kpiDetail.noData')}</p>
      )}
    </div>
  );
};

const LotsContent: React.FC<{ lots: Lot[]; loading: boolean; byproducts: ByproductSummaryItem[]; byproductsLoading: boolean; t: (key: string) => string }> = ({ lots, loading, byproducts, byproductsLoading, t }) => {
  if (loading || byproductsLoading) {
    return (
      <div className="flex items-center justify-center py-8 gap-2 text-gray-500 dark:text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        {t('manager.kpiDetail.loading')}
      </div>
    );
  }

  const availableLots = lots.filter(lot => Number(lot.current_amount) > 0);

  // Build unified rows: regular lots + byproducts in one table
  type UnifiedRow = { key: string; item_number: string; item_name: string; weight: number; cases: string; location: string; received_date: string | null };
  const rows: UnifiedRow[] = [
    ...availableLots.map(lot => {
      // WCO-2594: remaining cases derived from live weight (actual_quantity is the received count and
      // never decrements). Packing-aware. null → no case basis (weight-only lot) → show '—'.
      const remainingCases = displayRemainingCases(lot);
      return {
        key: `lot-${lot.id}`,
        item_number: lot.item_number,
        item_name: lot.item_name || '—',
        weight: Number(lot.current_amount),
        cases: remainingCases != null ? remainingCases.toLocaleString() : '—',
        location: lot.location_name || lot.location_code || '—',
        received_date: lot.received_date,
      };
    }),
    ...byproducts.map(bp => ({
      key: `bp-${bp.item_number}`,
      item_number: bp.item_number,
      item_name: bp.item_name || '—',
      weight: Number(bp.total_weight),
      cases: Number(bp.case_count) > 0 ? Number(bp.case_count).toLocaleString() : '—',
      location: 'HTE',
      received_date: bp.created_at,
    })),
  ];

  if (rows.length === 0) return <p className="text-gray-500 dark:text-gray-400 text-center py-6">{t('manager.kpiDetail.noData')}</p>;

  return (
    <TableWrapper>
      <thead className="bg-gray-50 dark:bg-gray-700/50">
        <tr>
          <Th>{t('manager.kpiDetail.rowNumber')}</Th>
          <Th>{t('manager.kpiDetail.itemNumber')}</Th>
          <Th>{t('manager.kpiDetail.itemName')}</Th>
          <Th>{t('manager.kpiDetail.weightLbs')}</Th>
          <Th>{t('manager.kpiDetail.cases')}</Th>
          <Th>{t('manager.kpiDetail.location')}</Th>
          <Th>{t('manager.kpiDetail.receivedDate')}</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
        {rows.map((row, index) => (
          <tr key={row.key} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
            <Td className="text-xs text-gray-400">{index + 1}</Td>
            <Td className="font-mono text-xs">{row.item_number}</Td>
            <Td>{row.item_name}</Td>
            <Td>{row.weight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Td>
            <Td>{row.cases}</Td>
            <Td>{row.location}</Td>
            <Td>{formatShortDate(row.received_date)}</Td>
          </tr>
        ))}
      </tbody>
    </TableWrapper>
  );
};

const OutputContent: React.FC<{ sessions: any[]; t: (key: string) => string }> = ({ sessions, t }) => {
  if (!sessions.length) return <p className="text-gray-500 dark:text-gray-400 text-center py-6">{t('manager.kpiDetail.noData')}</p>;

  return (
    <TableWrapper>
      <thead className="bg-gray-50 dark:bg-gray-700/50">
        <tr>
          <Th>{t('manager.kpiDetail.rowNumber')}</Th>
          <Th>{t('manager.kpiDetail.product')}</Th>
          <Th>{t('manager.kpiDetail.subSessions')}</Th>
          <Th>{t('manager.kpiDetail.outputBoxes')}</Th>
          <Th>{t('manager.kpiDetail.expectedBoxes')}</Th>
          <Th>{t('manager.kpiDetail.completionPct')}</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
        {sessions.map((s: any, index: number) => {
          const subs = (s.sub_sessions || []).filter((sub: any) => sub.status !== 'cancelled');
          const output = subs.reduce((sum: number, sub: any) => sum + (Number(sub.boxes_produced) || 0), 0);
          const expected = subs.reduce((sum: number, sub: any) => sum + (Number(sub.expected_boxes) || 0), 0);
          const pct = expected > 0 ? (output / expected) * 100 : 0;

          return (
            <tr key={s.session_number} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
              <Td className="text-xs text-gray-400">{index + 1}</Td>
              <Td>
                <div className="flex flex-col">
                  <span className="font-medium">{s.product_name || s.product_sku}</span>
                  <span className="text-xs text-gray-400 font-mono">{s.session_number}</span>
                </div>
              </Td>
              <Td>{subs.length}</Td>
              <Td className="font-medium">{output.toLocaleString()}</Td>
              <Td>{expected.toLocaleString()}</Td>
              <Td>
                <div className="flex items-center gap-2">
                  <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full ${pct >= 100 ? 'bg-green-500' : pct >= 50 ? 'bg-blue-500' : 'bg-orange-500'}`}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium">{expected > 0 ? `${pct.toFixed(2)}%` : '—'}</span>
                </div>
              </Td>
            </tr>
          );
        })}
      </tbody>
    </TableWrapper>
  );
};

// ── Modal config per type ────────────────────────────────────────────────
const modalConfig: Record<string, { titleKey: string; icon: React.ElementType; color: string }> = {
  sessions: { titleKey: 'manager.kpiDetail.sessionsTitle', icon: ClipboardList, color: 'indigo' },
  workforce: { titleKey: 'manager.kpiDetail.workforceTitle', icon: Users, color: 'cyan' },
  lots: { titleKey: 'manager.kpiDetail.lotsTitle', icon: Package, color: 'orange' },
  output: { titleKey: 'manager.kpiDetail.outputTitle', icon: TrendingUp, color: 'emerald' },
};

const colorClasses: Record<string, { iconBg: string; iconText: string }> = {
  indigo: { iconBg: 'bg-indigo-100 dark:bg-indigo-900/30', iconText: 'text-indigo-600 dark:text-indigo-400' },
  cyan: { iconBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconText: 'text-cyan-600 dark:text-cyan-400' },
  orange: { iconBg: 'bg-orange-100 dark:bg-orange-900/30', iconText: 'text-orange-600 dark:text-orange-400' },
  emerald: { iconBg: 'bg-emerald-100 dark:bg-emerald-900/30', iconText: 'text-emerald-600 dark:text-emerald-400' },
};

// ── Main component ───────────────────────────────────────────────────────
const KpiDetailModal: React.FC<KpiDetailModalProps> = ({
  type,
  onClose,
  todaySessions,
  workforce,
  lots,
  lotsLoading,
  byproducts = [],
  byproductsLoading = false,
}) => {
  const { t } = useLanguage();

  if (!type) return null;

  const config = modalConfig[type];
  const colors = colorClasses[config.color];
  const Icon = config.icon;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-3xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${colors.iconBg}`}>
                <Icon className={`w-5 h-5 ${colors.iconText}`} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t(config.titleKey)}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[65vh] overflow-y-auto">
            {type === 'sessions' && <SessionsContent sessions={todaySessions} t={t} />}
            {type === 'workforce' && <WorkforceContent workforce={workforce} t={t} />}
            {type === 'lots' && <LotsContent lots={lots} loading={lotsLoading} byproducts={byproducts} byproductsLoading={byproductsLoading} t={t} />}
            {type === 'output' && <OutputContent sessions={todaySessions} t={t} />}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KpiDetailModal;
